import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SavedUrlService } from 'src/app/core/services/saved-url.service';
import { AuthStoreService } from '../../shared/services/auth/auth-store.service';
import { TokenService } from '../services/token.service';
import { AuthState } from '../../shared/models/auth/auth-state.model';
import { AppCookiesService } from '../../core/services/app-cookies.service';
import { OnboardingService } from '../../onboarding/services/onboarding.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticatedGuard implements CanActivate {
  constructor(
    private auth: AuthStoreService,
    private router: Router,
    private tokenService: TokenService,
    private savedUrlService: SavedUrlService,
    private appCookiesService: AppCookiesService,
    private onboardingService: OnboardingService,
  ) {
  }

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean> {
    let loginUrl = '/auth/login?';
    route.queryParamMap.keys.forEach((key) => (loginUrl += `${key}=${route.queryParamMap.get(key)}&`));
    this.setMobileTokens(route);

    if (!this.auth.isAuthenticated) {
      this.savedUrlService.setUrlIfNotBlacklisted(state.url);

      const { tokenExpired } = this.tokenService;
      const { refreshTokenExpired } = this.tokenService;
      const { userInfo } = this.auth;

      console.log(`[User token] ACCESS: ${!!this.tokenService.getToken()} exp: ${tokenExpired},
        REFRESH: ${!!this.tokenService.getRefreshToken()} exp: ${refreshTokenExpired}.
        [User info] ${!!userInfo}`);

      if (!tokenExpired && userInfo) {
        if (this.auth.userInfo.email_verified) {
          if (this.tokenService.rememberMe) {
            console.log(`User ${this.auth.userInfo.sub} is remember me`);
            return true;
          } else {
            console.log(`User ${this.auth.userInfo.sub} is not remember me`);
            this.tokenService.clearLocalStorageItems();
            this.router.navigate(['/auth/login'], { queryParams: route.queryParams });

            return false;
          }
        } else {
          console.log(`User ${this.auth.userInfo.sub} has not verified email`);
          if (this.auth.userInfo.pay_by_link) {
            this.router.navigate(['/billtech-pay/email-verification'], {
              queryParams: {
                payByLinkToken: this.auth.userInfo.pay_by_link,
              },
              queryParamsHandling: 'merge',
            });
            return true;
          }
          return this.router.parseUrl('/auth/emailVerification');
        }
      } else if (this.tokenService.rememberMe && !refreshTokenExpired) {
        console.log('REFRESH TOKEN', this.tokenService.getRefreshToken());
        return this.auth.refreshToken().pipe(
          map(() => {
            console.log(`User ${this.auth.userInfo.sub} is refreshed`);
            return true;
          }),
        );
      } else {
        return this.router.parseUrl(loginUrl);
      }
    }
    if (!this.onboardingService.state) {
      this.onboardingService.init().subscribe();
    }
    return true;
  }

  private setMobileTokens(route: ActivatedRouteSnapshot) {
    const MOBILE_ACCESS_TOKEN = 'mobile_access_token';
    const MOBILE_REFRESH_TOKEN = 'mobile_refresh_token';
    const MOBILE_REMEMBER_DEVICE = 'mobile_remember_device';

    const accessToken = route.queryParamMap.get(MOBILE_ACCESS_TOKEN);
    if (accessToken) {
      this.tokenService.setToken(accessToken);
      this.auth.setState(AuthState.AUTHENTICATED);
      this.appCookiesService.closeBanner();
    }

    const refreshToken = route.queryParamMap.get(MOBILE_REFRESH_TOKEN);
    if (refreshToken) {
      this.tokenService.setRefreshToken(refreshToken);
    }

    const rememberDevice = route.queryParamMap.get(MOBILE_REMEMBER_DEVICE);
    if (rememberDevice && rememberDevice === 'true') {
      this.tokenService.setRememberMe(true);
    }

    if (accessToken || refreshToken) {
      this.tokenService.setMobileApp(true);
    }
  }
}
